import { Grid } from '../core/Grid.js';
import { GameType } from '../persistence/types.js';
import { ScoreBreakdown } from '../core/Scoring.js';
import { createGameForValidation } from './GameRegistry.js';
import { isVortexGame } from './IVortexGame.js';
import { VortexHub } from '../vortex/VortexHub.js';

/**
 * Recomputed win/score for a serialized game state. These are derived from the
 * state and never persisted — see the recompute-on-read approach in
 * PersistenceManager.
 */
export interface GameOutcome {
    isWon: boolean;
    score: ScoreBreakdown | null;
}

const NOT_PLAYED: GameOutcome = { isWon: false, score: null };

/**
 * Recompute the outcome of a serialized game state. For Vortex grids, pass the
 * Hub data so gate-aware games (active gates live in the serialized state) score
 * correctly. Invalid/empty states yield {isWon:false, score:null}.
 */
export function scoreGameState(
    grid: Grid,
    gameType: GameType,
    serialized: string | null | undefined,
    vortexHubData?: string | null,
): GameOutcome {
    if (!serialized) return NOT_PLAYED;

    const game = createGameForValidation(gameType, grid);
    if (vortexHubData && isVortexGame(game)) {
        const hub = new VortexHub(grid);
        hub.deserialize(vortexHubData);
        game.setVortexData(hub.getAllGatePairs(), hub.getGates());
    }

    try {
        game.deserialize(serialized);
    } catch {
        return NOT_PLAYED;
    }
    return { isWon: game.checkWin(), score: game.getScore() };
}

/** Recompute whether a Vortex Hub is valid from its serialized data. */
export function isVortexHubValid(grid: Grid, vortexHubData?: string | null): boolean {
    if (!vortexHubData) return false;
    const hub = new VortexHub(grid);
    hub.deserialize(vortexHubData);
    return hub.validate().isValid;
}
